import React from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  // Simple markdown parser for common formatting
  const parseMarkdown = (text: string): string => {
    // First handle bullet points and preserve line structure
    const lines = text.split('\n');
    const processedLines = lines.map(line => {
      // Bullet points: - item or * item
      if (line.trim().match(/^[-*]\s+/)) {
        const content = line.trim().replace(/^[-*]\s+/, '');
        return `<li>${content}</li>`;
      }
      return line;
    });
    
    // Group consecutive list items into ul tags
    const groupedLines: string[] = [];
    let inList = false;
    
    processedLines.forEach(line => {
      if (line.startsWith('<li>')) {
        if (!inList) {
          groupedLines.push('<ul class="list-disc ml-6 space-y-1">');
          inList = true;
        }
        groupedLines.push(line);
      } else {
        if (inList) {
          groupedLines.push('</ul>');
          inList = false;
        }
        groupedLines.push(line);
      }
    });
    
    // Close any remaining list
    if (inList) {
      groupedLines.push('</ul>');
    }
    
    return groupedLines.join('\n')
      // Bold: **text** or __text__
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic: *text* or _text_
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Code: `text`
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
      // Links: [text](url)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-indigo-600 hover:text-indigo-800 underline" target="_blank" rel="noopener noreferrer">$1</a>')
      // Line breaks (but not within lists)
      .replace(/\n(?!<\/ul>|<ul|<li>)/g, '<br>');
  };

  const parsedContent = parseMarkdown(content);

  return (
    <div 
      className={`prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: parsedContent }}
    />
  );
};

export default MarkdownRenderer;